import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Chip,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff, People, CheckCircle } from '@mui/icons-material';
import { RegistrationFlow, UpdateRegistrationFlowBody } from '@ory/client';
import { getOrySdk } from '../../services/ory';
import { useAuth } from '../../context/AuthContext';

interface InviteData {
  valid: boolean;
  first_name: string;
  last_name: string;
  email: string;
  role: string;
  family_name: string;
  expired: boolean;
  already_registered: boolean;
}

export const JoinPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { syncSession } = useAuth();

  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState<InviteData | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Registration form state
  const [flow, setFlow] = useState<RegistrationFlow | null>(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  // Look up the invite
  useEffect(() => {
    if (!token) {
      setLoading(false);
      setError('Invalid invite link.');
      return;
    }

    fetch(`/familypal/v1/identity/invite/lookup/${token}`)
      .then((res) => res.json())
      .then((data: InviteData) => {
        setInvite(data);
        if (data.valid) {
          setFirstName(data.first_name || '');
          setLastName(data.last_name || '');
          setEmail(data.email || '');
        }
      })
      .catch(() => setError('Failed to load invite details.'))
      .finally(() => setLoading(false));
  }, [token]);

  // Create Kratos registration flow when invite is valid
  useEffect(() => {
    if (!invite?.valid) return;

    const sdk = getOrySdk();
    sdk.createBrowserRegistrationFlow({})
      .then((res) => setFlow(res.data))
      .catch(() => setError('Failed to initialize registration.'));
  }, [invite?.valid]);

  const getCsrfToken = useCallback((): string => {
    if (!flow) return '';
    for (const node of flow.ui.nodes) {
      if (node.type === 'input') {
        const attrs = node.attributes as any;
        if (attrs.name === 'csrf_token' && attrs.value) {
          return attrs.value;
        }
      }
    }
    return '';
  }, [flow]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!flow) return;

    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    const sdk = getOrySdk();

    const body: UpdateRegistrationFlowBody = {
      method: 'password',
      password,
      traits: {
        email,
        name: {
          first: firstName,
          last: lastName,
        },
      },
      csrf_token: getCsrfToken(),
    };

    try {
      await sdk.updateRegistrationFlow({
        flow: flow.id,
        updateRegistrationFlowBody: body,
      });

      setSuccess(true);

      // Sync session and redirect after a brief delay
      setTimeout(async () => {
        await syncSession();
        navigate('/', { replace: true });
      }, 1500);
    } catch (err: any) {
      const data = err?.response?.data;
      if (data?.ui?.messages?.length > 0) {
        setError(data.ui.messages.map((m: any) => m.text).join(' '));
      } else if (data?.ui?.nodes) {
        const nodeErrors = data.ui.nodes
          .flatMap((n: any) => n.messages || [])
          .filter((m: any) => m.type === 'error')
          .map((m: any) => m.text);
        if (nodeErrors.length > 0) {
          setError(nodeErrors.join(' '));
        } else {
          setError('Registration failed. Please try again.');
        }
        setFlow(data);
      } else {
        setError('Registration failed. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" bgcolor="#f8fafc">
        <CircularProgress />
      </Box>
    );
  }

  // Success state
  if (success) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" bgcolor="#f8fafc">
        <Container maxWidth="sm">
          <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
            <CheckCircle sx={{ fontSize: 64, color: '#22c55e', mb: 2 }} />
            <Typography variant="h5" fontWeight={700} gutterBottom>
              Welcome to FamilyPal!
            </Typography>
            <Typography color="text.secondary">
              You've joined {invite?.family_name}. Redirecting...
            </Typography>
          </Paper>
        </Container>
      </Box>
    );
  }

  // Expired invite
  if (invite && !invite.valid && invite.expired) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" bgcolor="#f8fafc">
        <Container maxWidth="sm">
          <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
            <Typography variant="h5" fontWeight={700} gutterBottom>
              Invite Expired
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              This invite has expired. Ask your family member to send a new one.
            </Typography>
            <Button variant="contained" component={RouterLink} to="/login">
              Go to Login
            </Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  // Already registered
  if (invite && !invite.valid && invite.already_registered) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" bgcolor="#f8fafc">
        <Container maxWidth="sm">
          <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
            <Typography variant="h5" fontWeight={700} gutterBottom>
              You Already Have an Account
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Sign in to access your family.
            </Typography>
            <Button variant="contained" component={RouterLink} to="/login">
              Sign In
            </Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  // Invalid invite
  if (!invite?.valid) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" bgcolor="#f8fafc">
        <Container maxWidth="sm">
          <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
            <Typography variant="h5" fontWeight={700} gutterBottom>
              Invalid Invite
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              {error || 'This invite link is not valid.'}
            </Typography>
            <Button variant="contained" component={RouterLink} to="/signup">
              Create Account
            </Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  // Valid invite — show registration form
  return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" bgcolor="#f8fafc">
      <Container maxWidth="sm">
        <Paper sx={{ p: 4, borderRadius: 3 }}>
          {/* Header */}
          <Box textAlign="center" mb={3}>
            <People sx={{ fontSize: 48, color: '#4F46E5', mb: 1 }} />
            <Typography variant="h5" fontWeight={700}>
              Join {invite.family_name}
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 1 }}>
              You've been invited to join the {invite.family_name} family on FamilyPal
            </Typography>
            {invite.role && (
              <Chip label={invite.role} sx={{ mt: 1 }} size="small" color="primary" variant="outlined" />
            )}
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              label="First Name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              fullWidth
              margin="normal"
              required
            />
            <TextField
              label="Last Name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              fullWidth
              margin="normal"
              required
            />
            <TextField
              label="Email"
              value={email}
              fullWidth
              margin="normal"
              disabled
              helperText="This is the email your invite was sent to"
            />
            <TextField
              label="Choose a Password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              fullWidth
              margin="normal"
              required
              autoFocus
              helperText="At least 8 characters"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              disabled={isSubmitting || !password || password.length < 8}
              sx={{
                mt: 2,
                py: 1.5,
                bgcolor: '#4F46E5',
                '&:hover': { bgcolor: '#4338CA' },
                borderRadius: 2,
                textTransform: 'none',
                fontSize: 16,
                fontWeight: 600,
              }}
            >
              {isSubmitting ? <CircularProgress size={24} color="inherit" /> : `Join ${invite.family_name}`}
            </Button>
          </form>

          <Box textAlign="center" mt={3}>
            <Typography variant="body2" color="text.secondary">
              Already have an account?{' '}
              <Typography
                component={RouterLink}
                to="/login"
                variant="body2"
                color="primary"
                sx={{ textDecoration: 'none' }}
              >
                Sign in
              </Typography>
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default JoinPage;
